import { impersonationLoginApi } from "@/api/services/admin/auth";
import { TImpersonationLoginReqDto } from "@/api/services/admin/auth/dtos/impersonation-login.dto";
import { useOrganizationProductUserListInfinite } from "@/api/services/admin/organization";
import { TOrganizationProductUserDto } from "@/api/services/admin/organization/dtos/get-organization-product-user-list.dto";
import { useEnum } from "@/api/services/global/enum";
import { Button } from "@/components/elements/Button";
import { EllipsisText } from "@/components/elements/EllipsisText";
import { InnerBox } from "@/components/elements/InnerBox";
import { Table } from "@/components/elements/Table";
import { DATE_FORMAT } from "@/constants/datetime";
import { EMPTY_STR } from "@/constants/string";
import { TProductLoginState } from "@/features/session/types/productLoginState";
import { TUserFirstLoginState } from "@/features/session/types/userFirstLoginState";
import { TUserLoginVerifyState } from "@/features/session/types/userLoginVerifyState";
import { useTable } from "@/hooks/useTable";
import { AppRoutes } from "@/routes/config";
import { background, button, text } from "@/theme/colors";
import { dayjs } from "@/utils/dayjs";
import { showError } from "@/utils/error";
import { findLabelByValue } from "@/utils/object";
import { openNewTab } from "@/utils/url";
import LoginRoundedIcon from "@mui/icons-material/LoginRounded";
import { Radio, Stack, SxProps, Theme, Typography } from "@mui/material";
import { MRT_ColumnDef } from "material-react-table";
import { FC, useMemo, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

type TUserLoginCardProps = {
  sx?: SxProps<Theme>;
};

export const UserLoginCard: FC<TUserLoginCardProps> = ({ sx }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const loginState = location.state as TProductLoginState | null;

  const [selectedUserId, setSelectedUserId] = useState("");
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  const {
    dataFormatter: users,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetching,
  } = useOrganizationProductUserListInfinite(loginState?.organizationId, { selectedProductCode: loginState?.productCode });
  const { data: userRoles } = useEnum("EUserRole");

  const columns = useMemo<MRT_ColumnDef<TOrganizationProductUserDto>[]>(
    () => [
      {
        id: "select",
        header: "",
        size: 48,
        Cell: ({ row }) => {
          const userId = String(row.original.id);
          return (
            <Radio
              size="small"
              checked={userId === selectedUserId}
              onChange={() => setSelectedUserId(userId)}
              sx={{ color: button.primary, "&.Mui-checked": { color: button.primary } }}
            />
          );
        },
      },
      {
        accessorKey: "fullName",
        header: "ユーザー名",
        size: 120,
        Cell: ({ row }) => <EllipsisText title={row.original.fullName || EMPTY_STR.TEXT} variant="body14" color={text.primary} maxWidth={104} />,
      },
      {
        accessorKey: "email",
        header: "メールアドレス",
        size: 240,
        Cell: ({ row }) => <EllipsisText title={row.original.email || EMPTY_STR.TEXT} variant="body14" color={text.secondary} maxWidth={220} />,
      },
      {
        accessorKey: "role",
        header: "権限",
        size: 120,
        Cell: ({ row }) => {
          const isAdminUser = row.original.isAdminUser;
          const userRole = row.original.role ? findLabelByValue(userRoles, row.original.role) : EMPTY_STR.TEXT;
          const value = isAdminUser ? "いろはな特権管理者" : userRole;
          return (
            <Typography variant="body14" color={text.primary}>
              {value}
            </Typography>
          );
        },
      },
      {
        accessorKey: "lastLoginAt",
        header: "最終アクセス",
        size: 120,
        Cell: ({ row }) => (
          <Typography variant="body14" color={text.secondary}>
            {row.original.lastLoginAt ? dayjs(row.original.lastLoginAt).format(DATE_FORMAT) : EMPTY_STR.TEXT}
          </Typography>
        ),
      },
    ],
    [selectedUserId, userRoles],
  );

  const { table } = useTable({
    columns,
    rows: users,
    infiniteScroll: { fetchNextPage, hasNextPage },
    onClickRow: (row) => setSelectedUserId(String(row.id)),
    muiTableBodyRowProps: ({ row }) =>
      String(row.original.id) === selectedUserId ? { sx: { "& .MuiTableCell-root": { backgroundColor: background.secondary } } } : {},
  });

  const handleLogin = async () => {
    const selectedUser = users.find((user) => String(user.id) === selectedUserId);
    if (!selectedUser || !loginState) return;

    const payload: TImpersonationLoginReqDto = {
      productCode: loginState.productCode,
      organizationId: Number(loginState.organizationId),
      productContractId: loginState.productId,
      userId: Number(selectedUser.id),
      email: selectedUser.email,
      loginPurposes: loginState.purposes,
      loginPurposeNote: loginState.otherText,
      isAdminUser: selectedUser.isAdminUser,
    };

    try {
      setIsLoggingIn(true);
      const data = await impersonationLoginApi(payload);

      // Already verified today: the OTP step was skipped server-side and the login is complete.
      if (data.verified) {
        if (data.requiredUserRegistration) {
          if (!data.userRegistrationToken) return;
          const firstLoginState: TUserFirstLoginState = {
            organizationId: loginState.organizationId,
            userRegistrationToken: data.userRegistrationToken,
            productCode: loginState.productCode,
            purposes: loginState.purposes,
            otherText: loginState.otherText,
          };
          navigate(AppRoutes.userFirstLogin, { state: firstLoginState, replace: true });
          return;
        }
        openNewTab(data.redirectUrl, `session:${loginState.productCode}`);
        navigate(AppRoutes.organizationSelect, { replace: true });
        return;
      }

      const verifyState: TUserLoginVerifyState = {
        loginPayload: payload,
        verificationEmail: data.verificationEmail ?? EMPTY_STR.TEXT,
        organizationId: loginState.organizationId,
        userFullName: selectedUser.fullName,
        isAdminUser: selectedUser.isAdminUser,
        productCode: loginState.productCode,
        purposes: loginState.purposes,
        otherText: loginState.otherText,
      };
      navigate(AppRoutes.userLoginVerify, { state: verifyState });
    } catch (error) {
      showError(error);
    } finally {
      setIsLoggingIn(false);
    }
  };

  if (!loginState?.productId) {
    return <Navigate to={AppRoutes.organizationSelect} replace />;
  }

  return (
    <InnerBox
      padding="32px"
      sx={{ minWidth: 764, gap: 4, ...sx }}
      title={
        <Typography variant="head20Bold" color={text.primary} width="100%" textAlign="center">
          ログインするユーザー
        </Typography>
      }
    >
      <Stack gap={3}>
        <Table table={table} isLoading={isLoading} isFetching={isFetching} maxHeight="45vh" />
        <Stack gap={2}>
          <Button
            variant="primary"
            sx={{
              height: "60px !important",
            }}
            fullWidth
            disabled={!selectedUserId || isLoggingIn}
            startIcon={<LoginRoundedIcon sx={{ fontSize: 18 }} />}
            onClick={handleLogin}
          >
            <Typography variant="body14Semi">選択したユーザーで顧客環境にログイン</Typography>
          </Button>
          <Button variant="ghost" fullWidth onClick={() => navigate(-1)}>
            <Typography variant="body14Semi">戻る</Typography>
          </Button>
        </Stack>
      </Stack>
    </InnerBox>
  );
};
